const UTM_STORAGE_KEY = 'utm_params';

export interface UtmParams {
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
}

const UTM_KEYS: (keyof UtmParams)[] = ['utm_source', 'utm_medium', 'utm_campaign'];

/**
 * Extract UTM parameters from the current URL.
 * Returns null if none are present.
 */
export function extractUtmFromUrl(): UtmParams | null {
	const params = new URLSearchParams(window.location.search);
	const utm: UtmParams = {};
	let found = false;

	for (const key of UTM_KEYS) {
		const value = params.get(key);
		if (value) {
			utm[key] = value.trim();
			found = true;
		}
	}

	return found ? utm : null;
}

/**
 * Save UTM params to sessionStorage. Only overwrites if new params are present
 * (first touch wins — don't overwrite if user navigates internally).
 */
export function saveUtmToStorage(utm: UtmParams): void {
	try {
		sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
	} catch {
		// sessionStorage unavailable (e.g. private browsing)
	}
}

/**
 * Get stored UTM params. Returns null if none saved.
 */
export function getUtmFromStorage(): UtmParams | null {
	try {
		const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (typeof parsed !== 'object' || parsed === null) return null;
		const result: UtmParams = {};
		let found = false;
		for (const key of UTM_KEYS) {
			if (typeof parsed[key] === 'string' && parsed[key]) {
				result[key] = parsed[key];
				found = true;
			}
		}
		return found ? result : null;
	} catch {
		return null;
	}
}

/**
 * Capture UTM params from URL and store them.
 * Call this once on app init. First touch wins — won't overwrite existing.
 */
export function captureUtmParams(): void {
	const existing = getUtmFromStorage();
	if (existing) return;

	const fromUrl = extractUtmFromUrl();
	if (fromUrl) {
		saveUtmToStorage(fromUrl);
	}
}
